/**
 * ChainVault WP - SPL Token Creator
 * Create Solana SPL tokens
 *
 * @package ChainVault
 * @since   1.0.0
 */
import React, { useState } from 'react';
import { Buffer } from 'buffer';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import toast from 'react-hot-toast';
import './LaunchpadPage.css';

window.Buffer = window.Buffer || Buffer;

export default function SPLCreatorPage() {
    const navigate = useNavigate();
    const { address, walletType, builtInWallet } = useWallet();
    const [ isDeploying, setIsDeploying ] = useState( false );
    const [ deployResult,setDeployResult] = useState( null );
    const [ uploadMethod, setUploadMethod ] = useState( 'url' ); /* url or wp */
    const [ form, setForm ] = useState({
        name: '', symbol: '', decimals: '6', supply: '',
        description: '', image: '',
        revokeFreeze: true, revokeMint: true,
    });

    const settings      = window.chainvaultSettings || {};
    const launchpadFees = settings.launchpadFees    || {};
    const baseFee       = parseFloat( launchpadFees.splCreation   || 0.3 );
    const freezeFee     = parseFloat( launchpadFees.splRevokeFreeze || 0.1 );
    const mintFee       = parseFloat( launchpadFees.splRevokeMint   || 0.1 );
    const totalFee      = ( baseFee + (form.revokeFreeze ? freezeFee : 0) + (form.revokeMint ? mintFee : 0) ).toFixed(2);

    const update = ( f, v ) => setForm( p => ( { ...p, [f]: v } ) );

    const openWpMedia = () => {
        if ( ! window.wp || ! window.wp.media ) {
            toast.error( 'WordPress media library not available.' );
            return;
        }
        const frame = window.wp.media({
            title:    'Select Token Image',
            button:   { text: 'Use this image' },
            multiple: false,
            library:  { type: 'image' },
        });
        frame.on( 'select', () => {
            const attachment = frame.state().get( 'selection' ).first().toJSON();
            update( 'image', attachment.url );
        });
        frame.open();
    };

    const handleCreate = async () => {
        if ( !address ) { toast.error( 'Connect your Solana wallet first.' ); return; }
        if ( !form.name )   { toast.error( 'Enter a token name.' );   return; }
        if ( !form.symbol ) { toast.error( 'Enter a token symbol.' ); return; }
        if ( !form.supply ) { toast.error( 'Enter a total supply.' ); return; }
        setIsDeploying( true );
        try {
            const web3 = await import( '@solana/web3.js' );
            const spl  = await import( '@solana/spl-token' );
            const isTestnet = localStorage.getItem( 'chainvault_testnet_mode' ) === '1';
        const solanaRpc = isTestnet ? 'https://api.devnet.solana.com' : 'https://api.mainnet-beta.solana.com';
        console.log( 'Using RPC:', solanaRpc );
        const connection = new web3.Connection( solanaRpc, 'confirmed' );
            const decimals   = parseInt( form.decimals ) || 6;
            const supply     = BigInt( Math.floor( parseFloat( form.supply ) ) ) * BigInt( Math.pow(10, decimals) ); /* eslint-disable-line no-undef */

            let payer;
            if ( builtInWallet && builtInWallet.solana && builtInWallet.solana.secretKey ) {
                payer = web3.Keypair.fromSecretKey( new Uint8Array( builtInWallet.solana.secretKey ) );
            } else {
                throw new Error( 'Built-in Solana wallet required for token creation. Set up your wallet in Settings first.' );
            }

            toast.loading( 'Creating your SPL token...', { id: 'spl' } );
            const mint = await spl.createMint( connection, payer, payer.publicKey, form.revokeFreeze ? null : payer.publicKey, decimals );
            await new Promise( r => setTimeout( r, 3000 ) ); /* Allow mint to confirm on-chain */
            const tokenAccount = await spl.getOrCreateAssociatedTokenAccount( connection, payer, mint, payer.publicKey );
            await spl.mintTo( connection, payer, mint, tokenAccount.address, payer, supply );

            /* Save metadata to WordPress then register on-chain via PHP relay */
            try {
                const mintAddress = mint.toBase58();
                const wpBase      = window.chainvaultSettings?.siteUrl || '/wp-json/chainvault/v1';
                const isTestnet   = localStorage.getItem( 'chainvault_testnet_mode' ) === '1';
                const network     = isTestnet ? 'devnet' : 'mainnet';

                /* Step 1: Save metadata JSON to WordPress and get hosted URI */
                const saveRes  = await fetch( wpBase + '/metadata/save', {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body:    JSON.stringify({
                        mint: mintAddress, name: form.name, symbol: form.symbol,
                        description: form.description || '', image: form.image || '', network,
                    }),
                });
                const saveData = await saveRes.json();
                if ( ! saveData.uri ) throw new Error( 'Metadata save failed' );
                const metadataUri = saveData.uri;
                console.log( 'Metadata URI:', metadataUri );

                /* Step 2: Build Metaplex CreateMetadataAccountV3 instruction */
                const { PublicKey, Transaction, TransactionInstruction } = web3;
                const META_PROG    = new PublicKey( 'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s' );
                const SYS_PROG     = new PublicKey( '11111111111111111111111111111111' );
                const RENT_SYSVAR  = new PublicKey( 'SysvarRent111111111111111111111111111111111' );
                const mintPubkey   = new PublicKey( mintAddress );

                const [ metadataPDA ] = PublicKey.findProgramAddressSync(
                    [ Buffer.from( 'metadata' ), META_PROG.toBytes(), mintPubkey.toBytes() ],
                    META_PROG
                );
                console.log( 'Metadata PDA:', metadataPDA.toBase58() );

                /* Borsh-encode CreateMetadataAccountV3 (discriminator = 33) */
                const nameB   = Buffer.from( form.name,   'utf8' );
                const symbolB = Buffer.from( form.symbol, 'utf8' );
                const uriB    = Buffer.from( metadataUri, 'utf8' );

                const buf = Buffer.alloc( 1 + 4 + nameB.length + 4 + symbolB.length + 4 + uriB.length + 2 + 1 + 1 + 1 + 1 + 1 );
                let o = 0;
                buf.writeUInt8( 33, o++ );
                buf.writeUInt32LE( nameB.length,   o ); o += 4; nameB.copy( buf, o );   o += nameB.length;
                buf.writeUInt32LE( symbolB.length, o ); o += 4; symbolB.copy( buf, o ); o += symbolB.length;
                buf.writeUInt32LE( uriB.length,    o ); o += 4; uriB.copy( buf, o );    o += uriB.length;
                buf.writeUInt16LE( 0, o ); o += 2; /* seller_fee_basis_points */
                buf.writeUInt8( 0, o++ );            /* no creators option */
                buf.writeUInt8( 0, o++ );            /* no collection option */
                buf.writeUInt8( 0, o++ );            /* no uses option */
                buf.writeUInt8( 1, o++ );            /* is_mutable = true */
                buf.writeUInt8( 0, o++ );            /* collection_details = None */

                const ix = new TransactionInstruction({
                    programId: META_PROG,
                    keys: [
                        { pubkey: metadataPDA,     isSigner: false, isWritable: true  },
                        { pubkey: mintPubkey,       isSigner: false, isWritable: false },
                        { pubkey: payer.publicKey,  isSigner: true,  isWritable: false },
                        { pubkey: payer.publicKey,  isSigner: true,  isWritable: true  },
                        { pubkey: payer.publicKey,  isSigner: true,  isWritable: false },
                        { pubkey: SYS_PROG,         isSigner: false, isWritable: false },
                        { pubkey: RENT_SYSVAR,      isSigner: false, isWritable: false },
                    ],
                    data: buf.slice( 0, o ),
                });

                /* Step 3: Sign tx and relay via PHP to bypass browser CSP */
                const tx = new Transaction();
                tx.add( ix );
                const { blockhash } = await connection.getLatestBlockhash( 'finalized' );
                tx.recentBlockhash = blockhash;
                tx.feePayer = payer.publicKey;
                tx.sign( payer );

                const txB64 = tx.serialize().toString( 'base64' );
                console.log( 'Sending via relay, tx size:', txB64.length );

                const relayRes  = await fetch( wpBase + '/solana/relay', {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body:    JSON.stringify({ transaction: txB64, network }),
                });
                const relayData = await relayRes.json();

                if ( relayData.error ) throw new Error( JSON.stringify( relayData.error ) );
                console.log( 'Metadata tx:', relayData.signature );
                toast.success( 'Metadata registered on-chain!', { duration: 3000 } );

            } catch( metaErr ) {
                console.error( 'Metadata failed:', metaErr );
                toast.error( 'Metadata: ' + (metaErr.message||'').slice(0,80), { duration: 8000 } );
            }

            if ( form.revokeMint ) {
                await spl.setAuthority( connection, payer, mint, payer, spl.AuthorityType.MintTokens, null );
            }


            toast.success( 'SPL Token created!', { id: 'spl', duration: 8000 } );
            setDeployResult({ address: mint.toBase58(), name: form.name, symbol: form.symbol });
        } catch(err) {
            toast.dismiss( 'spl' );
            toast.error( (err.message || 'Creation failed').slice(0,100) );
        } finally {
            setIsDeploying( false );
        }
    };

    if ( deployResult ) {
        return (
            <div className="lp-page">
                <div className="lp-back" onClick={()=>navigate('/launchpad')}>← Back to Launchpad</div>
                <div className="lp-success-card">
                    <div className="lp-success-icon">🎉</div>
                    <h2>SPL Token Created!</h2>
                    <p><strong>{ deployResult.name } ({ deployResult.symbol })</strong> is live on Solana</p>
                    <div className="lp-result-address">
                        <span>Mint Address</span>
                        <code>{ deployResult.address }</code>
                        <button onClick={()=>{navigator.clipboard.writeText(deployResult.address);toast.success('Copied!');}}>Copy</button>
                    </div>
                    <a href={(localStorage.getItem('chainvault_testnet_mode')==='1' ? 'https://explorer.solana.com/address/' : 'https://solscan.io/token/') + deployResult.address + (localStorage.getItem('chainvault_testnet_mode')==='1' ? '?cluster=devnet' : '')} target="_blank" rel="noopener noreferrer" className="lp-btn-primary">View on Solscan ↗</a>
                    <button className="lp-btn-outline" onClick={()=>{setDeployResult(null);setForm({name:'',symbol:'',decimals:'6',supply:'',description:'',image:'',revokeFreeze:true,revokeMint:true});}}>Create Another</button>
                </div>
            </div>
        );
    }

    return (
        <div className="lp-page">
            <div className="lp-back" onClick={()=>navigate('/launchpad')}>← Back to Launchpad</div>
            <div className="lp-creator-wrap">
                <div className="lp-creator-main">
                    <div className="lp-creator-header">
                        <div className="lp-creator-badge">SPL TOKEN CREATOR</div>
                        <h1>Create and publish a Solana token</h1>
                        <p>Create your SPL token on the Solana blockchain. Fast, cheap and no coding required.</p>
                    </div>
                    <div className="lp-form-card">
                        <div className="lp-section-title">Token Information</div>
                        <div className="lp-form-grid">
                            <div className="lp-field"><label>Token Name *</label><input className="lp-input" placeholder="e.g. My Token" value={form.name} onChange={e=>update('name',e.target.value)} /></div>
                            <div className="lp-field"><label>Symbol * <span className="lp-hint-inline">(max 8 chars)</span></label><input className="lp-input" placeholder="e.g. MTK" value={form.symbol} onChange={e=>update('symbol',e.target.value.toUpperCase())} maxLength={8} /></div>
                            <div className="lp-field"><label>Decimals</label><input className="lp-input" type="number" value={form.decimals} onChange={e=>update('decimals',e.target.value)} min="0" max="9" /><span className="lp-hint">Default recommended: 6</span></div>
                            <div className="lp-field"><label>Total Supply *</label><input className="lp-input" type="number" placeholder="e.g. 1000000" value={form.supply} onChange={e=>update('supply',e.target.value)} /></div>
                        </div>
                        <div className="lp-field"><label>Description</label><textarea className="lp-input" rows="3" placeholder="Brief description..." value={form.description} onChange={e=>update('description',e.target.value)} /></div>
                        <div className="lp-field">
                            <label>Token Image</label>
                            <div style={{display:'flex',gap:'8px',marginBottom:'10px'}}>
                                <button type="button" onClick={()=>setUploadMethod('wp')} style={{flex:1,padding:'8px',background:uploadMethod==='wp'?'rgba(124,99,255,0.3)':'rgba(255,255,255,0.05)',border:'1px solid',borderColor:uploadMethod==='wp'?'#7C63FF':'rgba(255,255,255,0.1)',borderRadius:'8px',color:uploadMethod==='wp'?'#7C63FF':'#888',fontWeight:'600',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'}}>📁 WordPress Media</button>
                                <button type="button" onClick={()=>setUploadMethod('url')} style={{flex:1,padding:'8px',background:uploadMethod==='url'?'rgba(124,99,255,0.3)':'rgba(255,255,255,0.05)',border:'1px solid',borderColor:uploadMethod==='url'?'#7C63FF':'rgba(255,255,255,0.1)',borderRadius:'8px',color:uploadMethod==='url'?'#7C63FF':'#888',fontWeight:'600',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'}}>🔗 Image URL</button>
                            </div>
                            { uploadMethod === 'wp' ? (
                                <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                                    <button type="button" onClick={openWpMedia} className="lp-btn-outline" style={{padding:'10px 16px',fontSize:'13px'}}>📁 Open Media Library</button>
                                    { form.image && <img src={form.image} alt="token" style={{width:'48px',height:'48px',borderRadius:'8px',objectFit:'cover',border:'1px solid rgba(255,255,255,0.1)'}} /> }
                                    { form.image && <span style={{fontSize:'12px',color:'#00e5a0'}}>✓ Image selected</span> }
                                </div>
                            ) : (
                                <input className="lp-input" placeholder="https://your-image-url.png" value={form.image} onChange={e=>update('image',e.target.value)} />
                            ) }
                            { form.image && uploadMethod === 'url' && <img src={form.image} alt="preview" style={{width:'48px',height:'48px',borderRadius:'8px',objectFit:'cover',marginTop:'8px',border:'1px solid rgba(255,255,255,0.1)'}} onError={e=>e.target.style.display='none'} /> }
                        </div>
                        <div className="lp-section-title" style={{marginTop:'24px'}}>Token Authority</div>
                        { [
                            { key:'revokeFreeze', label:'Revoke Freeze Authority', desc:'Required to create a liquidity pool. Cost: '+freezeFee+' SOL', required: true },
                            { key:'revokeMint',   label:'Revoke Mint Authority',   desc:'Ensures no more tokens can be minted. Cost: '+mintFee+' SOL' },
                        ].map( opt => (
                            <div key={opt.key} className="lp-toggle-row">
                                <div className="lp-toggle-info">
                                    <strong>{ opt.label }{ opt.required && ' (required)' }</strong>
                                    <p>{ opt.desc }</p>
                                </div>
                                <label className="lp-toggle">
                                    <input type="checkbox" checked={form[opt.key]} onChange={e=>update(opt.key,e.target.checked)} />
                                    <span className="lp-toggle-slider" />
                                </label>
                            </div>
                        ) ) }
                        { address
                            ? <button className="lp-btn-primary lp-btn-solana" onClick={handleCreate} disabled={isDeploying}>{ isDeploying ? 'Creating Token...' : 'Create SPL Token' }</button>
                            : <button className="lp-btn-primary lp-btn-solana" onClick={()=>toast.error('Connect your Solana wallet in Settings first')}>Connect Solana Wallet</button>
                        }
                        <div className="lp-total-cost">Total cost: { totalFee } SOL</div>
                    </div>
                </div>
                <div className="lp-creator-side">
                    <div className="lp-fee-card lp-fee-solana">
                        <div className="lp-fee-label">PLATFORM FEE</div>
                        <div className="lp-fee-amount">{ totalFee } <span>SOL</span></div>
                        <div className="lp-fee-network">Network: Solana Mainnet</div>
                    </div>
                    <div className="lp-guide-card">
                        <h3>How to Create a Solana Token</h3>
                        <ol>
                            <li>Connect your Phantom or built-in Solana wallet</li>
                            <li>Enter your token name and symbol (max 8 chars)</li>
                            <li>Set decimals (default 6 for Solana)</li>
                            <li>Set your total supply</li>
                            <li>Upload your token image URL</li>
                            <li>Choose Revoke options</li>
                            <li>Click Create and approve in your wallet</li>
                        </ol>
                    </div>
                    <div className="lp-info-box">
                        <h4>Why Solana?</h4>
                        <p>Solana offers extremely fast transactions and very low fees compared to Ethereum, making it perfect for token creation.</p>
                    </div>
                    <div className="lp-info-box">
                        <h4>All Transactions Are Final</h4>
                        <p>We do not issue refunds. Please double-check all details before creating your token.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
